import {
  Box,
  Grid,
  InputLabel,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC } from "react";
import useResponsive from "../../hooks/useResponsive";

import { InputCustomSize, TextArea } from "../UIElements/input-ui";
import SelectInput from "../UIElements/select";
import { JobIdDetailsProps } from "../../interface/job_board";
import { labelStyles, textSty } from "../UIElements/styles-input";
import {
  extractContractDates,
  formatDate,
  formatTextType,
  separateDocsByComma,
} from "../../utils/url-id";

const JobDetails: FC<{ jobDetails: JobIdDetailsProps }> = ({ jobDetails }) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  
  return (
    <Box sx={{}}>
      <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle2">
        Job Type
      </Typography>
      <Box>
        <Stack direction={"row"} sx={{ gap: 2, my: 2 }}>
          <Typography sx={{ ...labelStyles, mb: 3 }}>Job Type</Typography>
          <Typography sx={{ ...textSty }}>
            {" "}
            {formatTextType(jobDetails?.job_type ?? "")}
          </Typography>
        </Stack>
        <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
          Job Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>Job Title</Typography>
            <Typography sx={{ ...textSty }}>{jobDetails?.job_title}</Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>Department</Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.department ?? "")}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ width: "100%", my: 2 }}>
          <Typography sx={{ ...labelStyles, mb: 0 }}>
            Job Description:
          </Typography>
          <Typography sx={{ ...textSty }}>
            {jobDetails?.job_description}
          </Typography>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          {/* <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>Qualifications</Typography>
              <Typography sx={{ ...textSty }}>
                {jobDetails?.quali}
              </Typography>
            </Stack> */}
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Experience Level:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.experience_level)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/*  */}
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Employment Type & Schedule
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Employment Type:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.employment_type)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Work Schedule:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.work_schedule)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Contract Duration (For Temporary Jobs):
            </Typography>
            <Typography sx={{ ...textSty }}>
              {extractContractDates(jobDetails?.contract_duration ?? "")}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Location & Compensation
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Work Location:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.work_location ?? "")}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Salary Range:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.salary_range)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Additional Benefits:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(jobDetails?.additional_info ?? "")}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Application Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Application Deadline:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {formatDate(jobDetails?.application_deadline)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              Required Documents:
            </Typography>
            {separateDocsByComma?.(jobDetails?.required_documents)?.map(
              (val, id) => (
                <Typography key={id} sx={{ ...textSty }}>
                  [✓] {val}
                </Typography>
              )
            )}
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: "100%" }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>
              How to aaply:
            </Typography>
            <Typography sx={{ ...textSty }}>
              {jobDetails?.how_to_apply ?? "-"}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default JobDetails;
